import { Button } from '@/components/ui/button';
import { useState, useEffect, useRef } from 'react';
import {
  Mail, Phone, MapPin, Instagram, Facebook, Twitter, Youtube,
  Linkedin, Music, Calendar, ExternalLink, Play, ChevronRight, ChevronLeft, Star, Quote,
  User, ArrowRight, X, Plus, Briefcase, Users, HelpCircle, MessageSquare, CheckCircle,
  Pause
} from 'lucide-react';

import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { AnimatedSection } from './AnimatedSection';

const THEMES = {
  dark_stage: {
    bg: 'bg-slate-900',
    text: 'text-white',
    textMuted: 'text-slate-400',
    card: 'bg-slate-800',
    border: 'border-slate-700',
    accent: 'text-orange-500',
    button: 'bg-orange-500 hover:bg-orange-400',
  },
  theatre_red: {
    bg: 'bg-[#1a0c0e] tx-velvet',
    text: 'text-[#f8f0e6]',
    textMuted: 'text-[#bfa39a]',
    card: 'bg-[#120709] tx-velvet-alt',
    border: 'border-[#3b2125]',
    accent: 'text-[#d4af37]',
    button: 'bg-[#b91c1c] hover:bg-[#dc2626]',
  },
  ivory_gold: {
    bg: 'bg-[#faf7f1]',
    text: 'text-[#1c1710]',
    textMuted: 'text-[#8d7f68]',
    card: 'bg-[#f1ebdd]',
    border: 'border-[#e2d8c3]',
    accent: 'text-[#b08d2f]',
    button: 'bg-[#1c1710] hover:bg-[#3a2f1e]',
  },
  light_modern: {
    bg: 'bg-white',
    text: 'text-slate-900',
    textMuted: 'text-slate-500',
    card: 'bg-slate-50',
    border: 'border-slate-200',
    accent: 'text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-500',
  },
  colorful_kids: {
    bg: 'bg-amber-50',
    text: 'text-purple-900',
    textMuted: 'text-purple-600',
    card: 'bg-white',
    border: 'border-pink-200',
    accent: 'text-pink-500',
    button: 'bg-pink-500 hover:bg-pink-400',
  },
  elegant_classic: {
    bg: 'bg-stone-900',
    text: 'text-stone-50',
    textMuted: 'text-stone-400',
    card: 'bg-stone-800',
    border: 'border-stone-700',
    accent: 'text-amber-400',
    button: 'bg-amber-500 hover:bg-amber-400',
  },
  bold_artist: {
    bg: 'bg-zinc-900',
    text: 'text-white',
    textMuted: 'text-zinc-400',
    card: 'bg-zinc-800',
    border: 'border-zinc-700',
    accent: 'text-red-500',
    button: 'bg-red-500 hover:bg-red-400',
  },
};

// Social icon mapper
const getSocialIcon = (platform) => {
  const name = platform.toLowerCase();
  if (name.includes('instagram')) return Instagram;
  if (name.includes('facebook')) return Facebook;
  if (name.includes('twitter') || name.includes('x')) return Twitter;
  if (name.includes('youtube')) return Youtube;
  if (name.includes('linkedin')) return Linkedin;
  if (name.includes('spotify') || name.includes('music')) return Music;
  return ExternalLink;
};


export const HeroSection = ({ data, theme, performerName, tagline, primaryColor, isLoaded }) => {
  const t = THEMES[theme] || THEMES.dark_stage;
  const bgImage = data?.background_image;
  const focalPoint = data?.background_focal_point || { x: 50, y: 50 };
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return (
    <section 
      className={cn("relative min-h-screen flex items-center justify-center overflow-hidden", t.bg)}
    >
      {/* Background Image with parallax */}
      {bgImage && (
        <div 
          className="absolute inset-0 transition-all ease-out"
          style={{ 
            backgroundImage: `url(${bgImage})`,
            backgroundSize: 'cover',
            backgroundPosition: `${focalPoint.x}% ${focalPoint.y}%`,
            opacity: isLoaded ? 1 : 0,
            transform: `scale(${isLoaded ? 1.1 : 1.2}) translateY(${scrollY * 0.3}px)`,
            transitionDuration: '1500ms',
            transitionProperty: 'opacity'
          }}
        />
      )}
      
      {/* Animated gradient overlay */}
      <div 
        className="absolute inset-0 transition-opacity"
        style={{
          background: bgImage 
            ? `linear-gradient(135deg, rgba(0,0,0,0.7) 0%, transparent 50%, rgba(0,0,0,0.8) 100%),
               linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.9) 100%)`
            : 'transparent',
          opacity: isLoaded ? 1 : 0,
          transitionDuration: '1000ms'
        }}
      />

      {/* Floating accent shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute w-96 h-96 rounded-full blur-3xl opacity-20"
          style={{ 
            background: primaryColor,
            top: '10%',
            right: '-10%',
            transform: `translateY(${scrollY * 0.1}px)`
          }}
        />
        <div 
          className="absolute w-64 h-64 rounded-full blur-3xl opacity-10"
          style={{ 
            background: primaryColor,
            bottom: '20%',
            left: '-5%',
            transform: `translateY(${-scrollY * 0.15}px)`
          }}
        />
      </div>
      
      {/* Content with staggered fade-in */}
      <div 
        className="relative z-10 text-center px-4 max-w-5xl"
        style={{ transform: `translateY(${scrollY * 0.2}px)` }}
      >
        {data?.logo ? (
          <img
            src={data.logo}
            alt={data?.headline || performerName}
            className="mx-auto mb-10 w-full max-w-md md:max-w-xl transition-all ease-out"
            style={{
              opacity: isLoaded ? 1 : 0,
              transform: isLoaded ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.92)',
              transitionDuration: '1500ms',
              transitionDelay: '200ms',
            }}
            data-testid="hero-logo"
          />
        ) : (
        <h1 
          className={cn(
            "text-5xl md:text-7xl lg:text-9xl font-bold mb-6 transition-all ease-out tracking-tight",
            bgImage ? 'text-white' : t.text
          )}
          style={{ 
            fontFamily: "'Outfit', sans-serif",
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0) scale(1)' : 'translateY(50px) scale(0.95)',
            transitionDuration: '1200ms',
            transitionDelay: '200ms',
            textShadow: '0 4px 30px rgba(0,0,0,0.5)'
          }}
        >
          {data?.headline || performerName}
        </h1>
        )}
        <p 
          className={cn(
            "text-xl md:text-2xl lg:text-3xl mb-12 transition-all ease-out font-light",
            bgImage ? 'text-white/90' : t.textMuted
          )}
          style={{ 
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(30px)',
            transitionDuration: '1000ms',
            transitionDelay: '500ms',
            letterSpacing: '0.05em'
          }}
        >
          {data?.subheadline || tagline}
        </p>
        {data?.cta_text && (
          <div
            className="transition-all ease-out"
            style={{ 
              opacity: isLoaded ? 1 : 0,
              transform: isLoaded ? 'translateY(0)' : 'translateY(30px)',
              transitionDuration: '1000ms',
              transitionDelay: '700ms'
            }}
          >
            <Button 
              className={cn(
                "rounded-full px-12 py-8 text-lg text-white shadow-2xl transition-all duration-300",
                "hover:scale-105 hover:shadow-3xl",
                t.button
              )}
              style={{ 
                backgroundColor: primaryColor,
                boxShadow: `0 20px 40px -10px ${primaryColor}80`
              }}
              onClick={() => {
                const link = data?.cta_link || '#contact';
                if (link.startsWith('#')) {
                  document.getElementById(link.slice(1))?.scrollIntoView({ behavior: 'smooth' });
                } else {
                  window.open(link, '_blank');
                }
              }}
              data-testid="hero-cta-button"
            >
              {data.cta_text}
            </Button>
          </div>
        )}
      </div>
      
      {/* Scroll indicator */}
      <div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2 transition-all"
        style={{ 
          opacity: isLoaded ? (scrollY > 100 ? 0 : 0.6) : 0,
          transform: `translateX(-50%) translateY(${isLoaded ? 0 : 20}px)`,
          transitionDuration: '1000ms',
          transitionDelay: '1200ms'
        }}
      >
        <div className="flex flex-col items-center gap-2">
          <span className={cn("text-xs uppercase tracking-widest", bgImage ? "text-white/50" : ['light_modern','colorful_kids','ivory_gold'].includes(theme) ? "text-black/40" : "text-white/50")}>Scroll</span>
          <div className={cn("w-6 h-10 border-2 rounded-full flex justify-center pt-2", bgImage ? "border-white/30" : ['light_modern','colorful_kids','ivory_gold'].includes(theme) ? "border-black/20" : "border-white/30")}>
            <div className={cn("w-1.5 h-3 rounded-full animate-bounce", bgImage ? "bg-white/60" : ['light_modern','colorful_kids','ivory_gold'].includes(theme) ? "bg-black/40" : "bg-white/60")} />
          </div>
        </div>
      </div>
    </section>
  );
};


